import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Search, X, ChevronRight } from 'lucide-react';
import { inputCls } from '../components/shared/FormKit';
import ResultCount from '../components/shared/ResultCount';
import { PAGE_SIZE, TablePager } from '../components/SortableTable';
import { formatDateShort } from '../utils/dateFormat';
import { fetchJSON } from '../utils/api';
import { EntityCategoryChip, RelationshipBadge } from '../components/shared/entityCategory';

/**
 * Client Hub — look a client up by company name, UEN or group name
 * (docs/modules/client-hub.md). One search box; every hit is a company row
 * that opens the client profile (/clients/:id). Matching groups show as chips
 * above the rows — clicking one lists that group's companies instead.
 *
 * Read-only by construction: the page only ever GETs. The search term lives in
 * the URL (?q=) so a result page can be bookmarked / shared. Results are
 * server-paged PAGE_SIZE at a time with the shared TablePager.
 */

const MIN_CHARS = 2;

const ClientHub = () => {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [term, setTerm] = useState(params.get('q') || '');
  const groupId = params.get('group') || '';
  const [result, setResult] = useState({ entities: [], groups: [], group: null, total: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searched, setSearched] = useState(false);
  const [page, setPage] = useState(1);
  const timer = useRef(null);

  // Debounced fetch off the URL state (q / group), not off keystrokes directly.
  const q = params.get('q') || '';
  // A new term or group starts from page 1.
  useEffect(() => { setPage(1); }, [q, groupId]);
  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    const useGroup = Boolean(groupId);
    if (!useGroup && q.trim().length < MIN_CHARS) {
      setResult({ entities: [], groups: [], group: null, total: 0 });
      setSearched(false);
      return undefined;
    }
    let cancelled = false;
    timer.current = setTimeout(async () => {
      setLoading(true);
      setError(null);
      try {
        const qs = new URLSearchParams({ page: String(page), pageSize: String(PAGE_SIZE) });
        if (useGroup) qs.set('groupId', groupId); else qs.set('q', q.trim());
        const data = await fetchJSON(`/api/client-hub/search?${qs.toString()}`);
        if (!cancelled) {
          setResult({
            entities: data.entities || [], groups: data.groups || [], group: data.group || null,
            total: typeof data.total === 'number' ? data.total : (data.entities || []).length,
          });
          setSearched(true);
        }
      } catch (e) {
        if (!cancelled) setError(e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 250);
    return () => { cancelled = true; };
  }, [q, groupId, page]);

  const onType = (v) => {
    setTerm(v);
    const next = new URLSearchParams();
    if (v.trim()) next.set('q', v);
    setParams(next, { replace: true });
  };

  const pickGroup = (g) => {
    const next = new URLSearchParams();
    if (term.trim()) next.set('q', term);
    next.set('group', String(g.id));
    setParams(next);
  };

  const clearGroup = () => {
    const next = new URLSearchParams();
    if (term.trim()) next.set('q', term);
    setParams(next);
  };

  const rows = result.entities;

  return (
    <div className="p-4 sm:p-6 max-w-5xl mx-auto">
      <div className="mb-6">
        <h1 className="text-xl sm:text-2xl font-bold text-slate-900">Client Hub</h1>
        <p className="text-sm text-slate-500 mt-1">
          Look a client up by company name, UEN or client group — master record, group, and every engagement up to its QC6 / QC7 form.
        </p>
      </div>

      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
        <div className="p-4 border-b border-slate-200">
          <div className="relative">
            <Search className="w-5 h-5 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              autoFocus
              value={term}
              onChange={(e) => onType(e.target.value)}
              placeholder="Company name, UEN or client group…"
              className={`${inputCls} pl-10 text-base`}
              aria-label="Search clients"
            />
          </div>
          {!groupId && q.trim().length > 0 && q.trim().length < MIN_CHARS && (
            <p className="text-xs text-slate-500 mt-2">Type at least {MIN_CHARS} characters.</p>
          )}
        </div>

        {/* Group hits — a chip per matching client group; pick one to list its companies. */}
        {!groupId && result.groups.length > 0 && (
          <div className="px-4 py-3 border-b border-slate-200 bg-slate-50">
            <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-2">Client Groups</p>
            <div className="flex flex-wrap gap-2">
              {result.groups.map((g) => (
                <button
                  key={g.id}
                  type="button"
                  onClick={() => pickGroup(g)}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-slate-300 bg-white text-sm text-slate-700 hover:bg-blue-50 hover:border-blue-300"
                >
                  {g.name}
                  <span className="text-xs text-slate-500">· {g.entityCount} {g.entityCount === 1 ? 'company' : 'companies'}</span>
                  {!g.active && <span className="text-[10px] uppercase text-slate-400">inactive</span>}
                </button>
              ))}
            </div>
          </div>
        )}

        {groupId && (
          <div className="px-4 py-3 border-b border-slate-200 bg-blue-50 flex flex-wrap items-center gap-2 text-sm">
            <span className="text-slate-700">Companies in <span className="font-semibold">{result.group ? result.group.name : '…'}</span></span>
            <button type="button" onClick={clearGroup} className="ml-auto inline-flex items-center gap-1 text-xs text-slate-600 hover:text-slate-900">
              <X className="w-3.5 h-3.5" /> Back to Search
            </button>
          </div>
        )}

        {error && <p className="px-4 py-3 text-sm text-red-600">{error}</p>}

        {(searched || loading) && (
          <div className="table-scroll">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide">
                <tr>
                  <th className="px-4 py-2.5">Company</th>
                  <th className="px-4 py-2.5">UEN</th>
                  <th className="px-4 py-2.5">Category</th>
                  <th className="px-4 py-2.5">Client Group</th>
                  <th className="px-4 py-2.5 text-right">Engagements</th>
                  <th className="px-4 py-2.5">Latest FYE</th>
                  <th className="px-4 py-2.5">Latest Status</th>
                  <th className="px-4 py-2.5" aria-label="Open" />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.length === 0 && !loading && (
                  <tr><td colSpan={8} className="px-4 py-8 text-center text-slate-500">No client matches “{groupId ? (result.group && result.group.name) : q}”.</td></tr>
                )}
                {rows.map((r) => (
                  <tr
                    key={r.id}
                    onClick={() => navigate(`/clients/${r.id}`)}
                    className="hover:bg-blue-50/60 cursor-pointer"
                  >
                    <td className="px-4 py-2.5">
                      <div className="flex items-center gap-2">
                        <Link to={`/clients/${r.id}`} className="font-medium text-slate-900 hover:text-blue-700" onClick={(e) => e.stopPropagation()}>
                          {r.entityName}
                        </Link>
                        <RelationshipBadge entity={r} />
                      </div>
                    </td>
                    <td className="px-4 py-2.5 font-mono text-xs text-slate-700">{r.uen}</td>
                    <td className="px-4 py-2.5"><EntityCategoryChip value={r.entityCategory} /></td>
                    <td className="px-4 py-2.5 text-slate-600">{r.clientGroupName || '—'}</td>
                    <td className="px-4 py-2.5 text-right text-slate-700">{r.engagementCount}</td>
                    <td className="px-4 py-2.5 text-slate-600 whitespace-nowrap">{r.latest ? formatDateShort(r.latest.yearEnd) : '—'}</td>
                    <td className="px-4 py-2.5">
                      {r.latest
                        ? <span className="text-slate-700">{r.latest.status}</span>
                        : <span className="text-slate-400">No engagement</span>}
                    </td>
                    <td className="px-4 py-2.5 text-right"><ChevronRight className="w-4 h-4 text-slate-400 inline" /></td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="flex items-center justify-end gap-3 px-4 py-2 border-t border-slate-200">
              <ResultCount count={result.total} noun="company" plural="companies" loading={loading} />
            </div>
            <TablePager
              page={page}
              setPage={setPage}
              total={result.total}
              className="flex items-center justify-between px-4 py-2 border-t border-slate-200"
            />
          </div>
        )}

        {!searched && !loading && !error && (
          <div className="px-4 py-12 text-center text-sm text-slate-500">
            Start typing to find a client.
          </div>
        )}
      </div>
    </div>
  );
};

export default ClientHub;
